import React, { useState, useEffect, useCallback } from 'react';
import {
  CircularProgress,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tfoot,
  useDisclosure,
} from '@chakra-ui/react';
import ReactPaginate from 'react-paginate';
import { Paper } from './atoms/Paper';
import { MdChevronRight, MdChevronLeft } from 'react-icons/md';
import { InterviewResultHistoryDetail } from '../pages/InterviewResult/InterviewResultHistoryDetail';

import { useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useGetMetadata } from '../hooks/useGetMetadata';
import { getInterviewResultAnswerHistoryDetail } from '../api/index';
import { GetAnswerHistoryDetail } from '../types/answerHistoryApiTypes';
import { Answer } from '../types/answer';
import { InterviewSheetHistory } from '../types/interviewSheetHistory';
import {
  toAnswers,
  toInterviewSheetHistory,
} from '../domains/toInterviewResultConfirm';

type Props = {
  answerHistoryList: DataType[];
  loading: boolean;
  showFooter?: boolean;
};

export type DataType = {
  interviewAnswerHistoryId: string;
  createdAt: string;
  createdByName: string;
};

export const InterviewAnswerHistoryListTable: React.VFC<Props> = ({
  answerHistoryList,
  loading,
  showFooter = true,
}) => {
  const { id } = useParams<{ id: string }>();
  const { user, getAccessTokenSilently } = useAuth0();
  const { auth0UserData } = useGetMetadata();
  const { tenantId } = auth0UserData;
  const userId = user?.sub ?? '';
  const userName = encodeURI(user?.name ?? '');
  const [apiLoading, setApiLoading] = useState(false);
  const itemsPerPage = 8;
  const [pageCount, setPageCount] = useState(0);
  const [itemOffset, setItemOffset] = useState(0);
  const [currentItems, setCurrentItems] = useState<DataType[]>();
  const [answers, setAnswers] = useState({} as Answer[]);
  const [interviewSheetHistory, setInterviewSheetHistory] = useState(
    {} as InterviewSheetHistory,
  );
  const [createdAt, setCreatedAt] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handlePageClick = useCallback(
    (event: { selected: number }) => {
      const newOffset =
        (event.selected * itemsPerPage) % answerHistoryList.length;
      setItemOffset(newOffset);
      setForcePage(event.selected);
    },
    [answerHistoryList.length],
  );

  const columns = [
    { columnName: '予約日時', show: true },
    { columnName: '氏名', show: true },
  ];
  const columnCount = columns.filter(({ show }) => show === true).length;
  const [forcePage, setForcePage] = useState(-1);

  useEffect(() => {
    const endOffset = itemOffset + itemsPerPage;
    setCurrentItems(answerHistoryList.slice(itemOffset, endOffset));
    setPageCount(Math.ceil(answerHistoryList.length / itemsPerPage));
  }, [itemOffset, itemsPerPage, answerHistoryList, handlePageClick]);

  const hadleGetHistoryDetail = (answerHistoryId: string) => {
    const getData = async (): Promise<void> => {
      try {
        setApiLoading(true);
        const accessToken = await getAccessTokenSilently({
          audience: 'https://www.webinterview.mic.jp/',
          scope: 'read:interview-results',
        });
        const result = await getInterviewResultAnswerHistoryDetail(
          tenantId,
          id,
          answerHistoryId,
          userId,
          userName,
          accessToken,
        );
        if (result.ok) {
          const data: GetAnswerHistoryDetail = await result.json();
          data.answers ? setAnswers(toAnswers(data.answers)) : ({} as Answer[]);
          data.interview_sheet_history
            ? setInterviewSheetHistory(
                toInterviewSheetHistory({
                  ...data.interview_sheet_history,
                  questions: data.interview_sheet_history.questions
                    .map((question) => ({
                      ...question,
                      question_items: question.question_items
                        .slice()
                        .sort((a, b) => a.sort_number - b.sort_number),
                    }))
                    .slice()
                    .sort((a, b) => a.sort_number - b.sort_number),
                }),
              )
            : ({} as InterviewSheetHistory);
          setCreatedAt(data.created_at);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setApiLoading(false);
      }
    };
    getData();
  };

  return (
    <Paper>
      <Table>
        <Thead>
          <Tr>
            <Th>更新日時</Th>
            <Th>更新者</Th>
          </Tr>
        </Thead>
        <Tbody>
          {loading ? (
            <Tr>
              <Td colSpan={columnCount}>
                <Flex justifyContent="center">
                  <CircularProgress isIndeterminate color="blue.300" />
                </Flex>
              </Td>
            </Tr>
          ) : (
            currentItems?.map((data, idx) => (
              <Tr
                role="group"
                key={idx}
                onClick={() => {
                  hadleGetHistoryDetail(data.interviewAnswerHistoryId);
                  onOpen();
                }}
                _hover={{ cursor: 'pointer' }}
              >
                <Td transition="all .2s ease" _groupHover={{ bg: 'blue.50' }}>
                  {data.createdAt}
                </Td>
                <Td transition="all .2s ease" _groupHover={{ bg: 'blue.50' }}>
                  {data.createdByName}
                </Td>
              </Tr>
            ))
          )}
        </Tbody>
        {showFooter ? (
          <Tfoot>
            <Tr>
              <Td colSpan={5}>
                <Flex justify="center">
                  <ReactPaginate
                    breakLabel="..."
                    nextLabel={<MdChevronRight />}
                    previousLabel={<MdChevronLeft />}
                    pageCount={pageCount}
                    marginPagesDisplayed={2}
                    pageRangeDisplayed={5}
                    onPageChange={handlePageClick}
                    containerClassName={'pagination'}
                    activeClassName={'active'}
                    previousClassName={'pagination__previous'}
                    nextClassName={'pagination__next'}
                    disabledClassName={'pagination__disabled'}
                    previousLinkClassName={'pagination__previous__link'}
                    nextLinkClassName={'pagination__next__link'}
                    forcePage={forcePage}
                  />
                </Flex>
              </Td>
            </Tr>
          </Tfoot>
        ) : (
          <></>
        )}
      </Table>
      <InterviewResultHistoryDetail
        isOpen={isOpen}
        onClose={onClose}
        answers={answers}
        questions={interviewSheetHistory.questions}
        createdAt={createdAt}
        loading={apiLoading}
      />
    </Paper>
  );
};
